import type { ChatMessageResponse } from 'app/api/ndaChatApi';
import type { FormDataShape, NdaType } from './types';

export const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const stripQuotes = (s: string): string =>
  s.replaceAll(/^(['"])(.*)\1$/g, '$2').trim();

const trimDelimiters = (s: string): string => {
  return stripQuotes(String(s).split(/[;\n]/)[0]).replace(/[.,]$/, '').trim();
};

const firstString = (...vals: unknown[]): string | null => {
  for (const v of vals) if (isNonEmptyString(v)) return v;
  return null;
};

const pickEmail = (val: unknown): string | null => {
  if (!isNonEmptyString(val)) return null;
  const re = /[\w.+-]+@[\w.-]+/;
  const m = re.exec(val);
  return m?.[0] ?? null;
};

const applyCompanyAndContact = (src: any, out: Partial<FormDataShape>) => {
  const company = firstString(
    src?.receivingParty,
    src?.receiving_party,
    src?.receiving,
    src?.xCompany,
    src?.counterparty,
    src?.counterpartyName,
    src?.counterparty_name
  );
  if (company) out.xCompany = trimDelimiters(company);
  if (isNonEmptyString(src?.xAddress))
    out.xAddress = trimDelimiters(src.xAddress);
  if (isNonEmptyString(src?.counterpartyAddress))
    out.xAddress = trimDelimiters(src.counterpartyAddress);
  if (isNonEmptyString(src?.xMail)) out.xMail = src.xMail;
  const cEmail = pickEmail(src?.counterpartyEmail);
  if (cEmail) out.xMail = cEmail;
};

const applyReceivingObject = (src: any, out: Partial<FormDataShape>) => {
  if (src?.receiving && typeof src.receiving === 'object') {
    const rx = src.receiving as Record<string, unknown>;
    const rAddr = firstString(rx.address, rx.addr, rx.location);
    const rMail = firstString(rx.email, rx.mail);
    const rSigner = firstString(rx.signer, rx.contact, rx.name);
    if (rAddr) out.xAddress = rAddr;
    if (rMail) out.xMail = rMail;
    if (rSigner) out.xSigner = rSigner;
  }
};

const applyZurichObject = (src: any, out: Partial<FormDataShape>) => {
  if (isNonEmptyString(src?.zurichAddress))
    out.zurichAddress = trimDelimiters(src.zurichAddress);
  if (isNonEmptyString(src?.zurichMail)) out.zurichMail = src.zurichMail;
  const zEmail = pickEmail(src?.zurichEmail);
  if (zEmail) out.zurichMail = zEmail;
  if (src?.disclosing && typeof src.disclosing === 'object') {
    const dz = src.disclosing as Record<string, unknown>;
    const zAddr = firstString(dz.address, dz.addr, dz.location);
    const zMail = firstString(dz.email, dz.mail);
    const zSigner = firstString(dz.signer, dz.contact, dz.name);
    if (zAddr) out.zurichAddress = zAddr;
    if (zMail) out.zurichMail = zMail;
    if (zSigner) out.zurichSigner = zSigner;
  }
  if (isNonEmptyString(src?.disclosing) && !out.zurichSigner) {
    out.zurichSigner = src.disclosing as string;
  }
};

const applyProjectAndDate = (src: any, out: Partial<FormDataShape>) => {
  const project = firstString(
    src?.projectDescription,
    src?.purpose,
    src?.project
  );
  if (project) {
    const cleaned = stripQuotes(project).trim();

    if (cleaned && cleaned.toLowerCase() !== 'description') {
      out.project = cleaned;
    }
  }
  const date = firstString(
    src?.effectiveDate,
    src?.effective_date,
    src?.date,
    src?.effective,
    src?.startDate
  );
  if (date) out.date = date;
};

const applySignersAndFlags = (src: any, out: Partial<FormDataShape>) => {
  if (isNonEmptyString(src?.xSigner)) out.xSigner = src.xSigner;
  if (isNonEmptyString(src?.counterpartySigner))
    out.xSigner = src.counterpartySigner;
  if (isNonEmptyString(src?.zurichSigner)) out.zurichSigner = src.zurichSigner;
  if (typeof src?.showDraftingNote === 'boolean')
    out.showDraftingNote = src.showDraftingNote;
};

const applyTypeIfPresent = (src: any, out: Partial<FormDataShape>) => {
  if (isNonEmptyString(src?.ndaType)) {
    const t = String(src.ndaType).toLowerCase();
    if (t === 'bilateral' || t === 'unilateral') out.ndaType = t as NdaType;
  }
};

export const mapVariablesToFormFields = (d: any): Partial<FormDataShape> => {
  if (!d || typeof d !== 'object') return {};
  const newFields: Partial<FormDataShape> = {};
  applyCompanyAndContact(d, newFields);
  applyReceivingObject(d, newFields);
  applyZurichObject(d, newFields);
  applyProjectAndDate(d, newFields);
  applySignersAndFlags(d, newFields);
  applyTypeIfPresent(d, newFields);
  return newFields;
};

export const mapAssistantTextToForm = (
  text: string = ''
): Partial<FormDataShape> => {
  const out: Partial<FormDataShape> = {};
  const safe = text;
  const getMatch = (label: string): string | null => {
    const re = new RegExp(String.raw`${label}[:\s]+([^;\n\.,]+)`, 'i');
    const m = re.exec(safe);
    return m?.[1]?.trim() ?? null;
  };
  const getMatchColon = (label: string): string | null => {
    const re = new RegExp(String.raw`${label}\s*:\s*(.+?)(?:[;\n\.,]|$)`, 'i');
    const m = re.exec(safe);
    return m?.[1]?.trim() ?? null;
  };
  const getName = (): string | null => {
    const re = /Counterparty\s+Name[:\s]+(.+?)(?:[;,\n.]|$)/i;
    const m = re.exec(safe);
    return m?.[1]?.trim() ?? null;
  };
  const getCounterpartyAddress = (): string | null => {
    const all = Array.from(
      safe.matchAll(/Counterparty\s+Address[:\s]+(.+?)(?:[;\n.]|$)/gi)
    );
    const last = all.at(-1) ?? null;
    return (last?.[1] ?? '').trim() || null;
  };
  const getEmail = (label: string): string | null => {
    const re = new RegExp(String.raw`${label}[:\s]+([\w.+-]+@[\w.-]+)`, 'i');
    const m = re.exec(safe);
    return m?.[1]?.trim() ?? null;
  };
  const getSigner = (label: string): string | null => {
    const re = new RegExp(String.raw`${label}[:\s]+(.+?)(?:[;,.\n]|$)`, 'i');
    const m = re.exec(safe);
    return m?.[1]?.trim() ?? null;
  };
  const getDate = (): string | null => {
    const patterns: RegExp[] = [
      /Date[:\s]+(\d{4}-\d{2}-\d{2})/i,
      /Date[:\s]+(\d{1,2}[-/]\d{1,2}[-/]\d{2,4})/i,
      /Date[:\s]+((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2},\s*\d{4})/i
    ];
    for (const re of patterns) {
      const m = re.exec(safe);
      const v = m?.[1]?.trim();
      if (v) return v;
    }
    return null;
  };
  const receiving = getMatch('Receiving|Counterparty Name|Counterparty');
  const disclosing = getMatch('Disclosing');
  const date = getDate() ?? getMatch('Date|Effective Date');
  const projectDesc = getMatchColon('Project Description');
  const purpose = projectDesc ?? getMatchColon('Purpose|Project');
  const xAddr = getCounterpartyAddress() ?? getMatch('Counterparty Address');
  const xMail =
    getEmail('Counterparty Email') ?? getMatch('Counterparty Email|Email');
  const zAddr = getMatch('Zurich Address');
  const zMail = getEmail('Zurich Email') ?? getMatch('Zurich Email');
  const xSigner =
    getSigner('Counterparty Signer') ?? getMatch('Counterparty Signer');
  const zSigner = getSigner('Zurich Signer') ?? getMatch('Zurich Signer');
  const name = getName() ?? receiving;
  if (name) out.xCompany = name;
  if (xAddr) out.xAddress = xAddr;
  if (xMail) out.xMail = xMail;
  if (zAddr) out.zurichAddress = zAddr;
  if (zMail) out.zurichMail = zMail;
  if (purpose && purpose.toLowerCase() !== 'description') out.project = purpose;
  if (date) out.date = date;
  if (xSigner) out.xSigner = xSigner;
  if (zSigner) out.zurichSigner = zSigner;
  if (!out.zurichSigner && disclosing) out.zurichSigner = disclosing;
  return out;
};

export const pickAssistantText = (resp: ChatMessageResponse): string => {
  let text: string = resp.reply ?? resp.answer ?? '';
  if (!text) {
    const hist = resp.session?.history;
    const lastAssistant = hist
      ?.slice()
      .reverse()
      .find((h) => h.role === 'assistant');
    text = lastAssistant?.content?.trim() ?? '';
  }
  if (!text) {
    text =
      'I captured your request. Please provide details like counterparty name, purpose, and effective date to proceed.';
  }
  return text;
};
